import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';

import { Icon } from '@components/index';
import { GROUPS, MEMBERS } from '@constants/routes';
import {
  DashboardCardGroupType,
  getDashboardCardData,
  getFormattedDateForChartAPI
} from '@features/dashboard/helper';
import useCanAccessDashboardApis from '@features/dashboard/hooks/useCanAccessDashboardApis';

import DashboardCard from '../dashboardCard/DashboardCard';

import { useLazyGetPaymentOverviewQuery } from '../../queries/index';
import { DateRangeType, PaymentDateFilterType } from '../../types';

interface DashboardCardProps {
  organizationId: string;
  dateRange: DateRangeType;
  selectedBranchId: string;
  showGrowth: boolean;
  isClickable?: boolean;
  selectedFilter: PaymentDateFilterType;
}

const DashboardCardsOverview = ({
  organizationId,
  dateRange,
  selectedBranchId,
  showGrowth,
  isClickable = true,
  selectedFilter
}: DashboardCardProps) => {
  const navigate = useNavigate();
  const canAccessDashboardApis = useCanAccessDashboardApis();

  const [dashBoardCardData, setDashBoardCardData] = useState<any>(
    getDashboardCardData({}, showGrowth)
  );

  const [getPaymentOverview, { isLoading, isFetching, isUninitialized }] =
    useLazyGetPaymentOverviewQuery();

  const handleGetAllPaymentOverview = async () => {
    if (!canAccessDashboardApis) return;

    const { startDate, endDate } = getFormattedDateForChartAPI(dateRange);

    const res = await getPaymentOverview({
      orgId: organizationId,
      queryString: `?from_timestamp=${startDate}&to_timestamp=${endDate}&branch_id=${selectedBranchId}&include_growth_percentage=${showGrowth}&time_filter_type=${selectedFilter}`
    });
    if ('data' in res) {
      setDashBoardCardData(getDashboardCardData(res.data, showGrowth));
    }
  };

  const showSkeleton =
    canAccessDashboardApis && (isLoading || isFetching || isUninitialized);

  const onClickGroupInfo = (id: DashboardCardGroupType) => {
    if (!isClickable) return;
    if (id === DashboardCardGroupType.ACTIVE_GROUPS) {
      navigate(GROUPS.HOME);
    } else {
      navigate(MEMBERS.MEMBERS);
    }
  };

  useEffect(() => {
    handleGetAllPaymentOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedBranchId, selectedFilter, canAccessDashboardApis]);

  const renderGroupInfo = () => (
    <div className="flex w-[48%] min-w-50 flex-col gap-2 lg:w-1/5">
      {dashBoardCardData?.groupInfo?.map((item: any) => (
        <div
          key={item.id}
          className={`flex h-full items-center justify-between gap-4 rounded-2xl border border-cofeeNeutral4 bg-cofeeNeutral1 p-2 xl:px-6 ${
            !isClickable ? 'pointer-events-none' : 'cursor-pointer'
          }`}
          onClick={() => onClickGroupInfo(item.id)}
          role="presentation"
        >
          <div className="flex items-center gap-2 py-1">
            <div className="flex size-8 items-center justify-center rounded-full border">
              <Icon name={item.icon} className="!size-4" />
            </div>
            <p className="font-medium text-cofeeNeutral8">{item.title}</p>
          </div>
          <div className="flex items-center ">
            {showSkeleton ? (
              <Skeleton
                height={32}
                width={32}
                key={Math.random()}
                animation="wave"
                variant="text"
              />
            ) : (
              <h2 className="text-xl font-semibold leading-8 text-cofeeGreyBlue900">
                {item.value}
              </h2>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex w-full flex-wrap gap-4 pt-6 lg:flex-nowrap">
      {dashBoardCardData?.cardValues?.map((item: any) => (
        <DashboardCard
          key={item.id}
          id={item.id}
          title={item.title}
          value={item.value}
          icon={item.icon}
          growthValue={item.growthValue}
          className={`flex w-[48%] flex-col justify-between lg:min-w-[25%] ${item.className}`}
          isLoading={showSkeleton}
          tooltipValue={item.tooltipValue}
          showGrowthValue={'growthValue' in item}
          isClickable={isClickable}
        />
      ))}
      {renderGroupInfo()}
    </div>
  );
};

export default DashboardCardsOverview;
